import { DynamicPriceIcon, J2Caution } from '@e3dc-react/icons'
import { TabButtonGroup } from 'src/Components/TabButton'
import { LineChartSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import CloseIcon from '@mui/icons-material/Close'
import { Box, DialogTitle, IconButton, Typography } from '@mui/material'
import React, { useContext, useState } from 'react'
import { DynamicPowerPricesEnabledContext } from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-context'
import { makeStyles } from 'tss-react/mui'
import usePowerPriceChartHeaderValues from '../../../../dynamic-power-prices/hooks/use-power-price-chart-header-values'
import { DynamicPricesSettings, DynamicPriceType, PowerPrice } from '../../../../dynamic-power-prices/types'
import PowerPriceChart from './PowerPriceChart'
import PowerPriceChartHeader from './PowerPriceChartHeader'
import PowerPriceLimitSettingsForm from './PowerPriceLimitSettingsForm'
import ResponsiveModalDrawerContainer from 'src/Elements/Utilities/ResponsiveModalDrawerContainer'
import { BannerLocations, BannerQueue } from 'src/Libs/banners'
import DynamicEmsChartLegend from './DynamicEmsChartLegend'
import { useDynamicEmsData } from 'src/Libs/dynamic-power-prices/hooks/dynamic-ems/use-dynamic-ems-data'
import { useApp } from 'src/Hooks'
import { toDateForModal } from 'src/Libs/dashboard-widgets/utils/dateUtils'
import { useIsDynamicEmsAccessible } from 'src/Libs/dynamic-power-prices'
import { POWER_PRICE_CHART_TO_PRINT_ID } from './power-price-chart.constants'

export interface PowerPriceChartModalProps {
  onClose?: () => void
  place?: number
  installationId: string | undefined
  storageId: string | undefined
  wallboxLimit: number
  batteryLimit: number
  hasInstallationWritingRights: boolean
  powerPrices?: PowerPrice[]
  currentPowerPriceValues: PowerPrice
  lastFetchedTimestamp: string
  isPowerPricesLoading: boolean
  dynamicPricesSettings: DynamicPricesSettings | null
  handleDynamicPricesSettingsUpdate: (newValues: DynamicPricesSettings) => Promise<void>
}

const useStyles = makeStyles()(theme => ({
  title: {
    padding: theme.spacing(4),
    paddingBottom: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',

    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      '& h2': {
        fontSize: 22,
      },
    },
  },
  icon: {
    [theme.breakpoints.down('sm')]: {
      fontSize: 30,
    },
  },
  body: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
      paddingInline: 0,
    },
  },
  powerPriceChartContainer: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    height: '100%',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'center',
    height: '20%',

    '& button': {
      width: 'auto',
      padding: theme.spacing(1, 4),
    },
  },
  buttonWrapper: {
    height: 'auto !important',
    marginBottom: theme.spacing(2),
  },
  noDataWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 300,
    height: '100%',
  },
}))

/**
 * Modal for detailed power price chart
 */
const DynamicPowerPriceModal: React.FC<PowerPriceChartModalProps> = ({
  onClose,
  wallboxLimit,
  batteryLimit,
  installationId,
  storageId,
  hasInstallationWritingRights,
  powerPrices,
  currentPowerPriceValues,
  lastFetchedTimestamp,
  isPowerPricesLoading,
  dynamicPricesSettings,
  handleDynamicPricesSettingsUpdate,
}) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { isDynamicPowerPriceEnabledForBattery, isDynamicPowerPriceEnabledForWallboxes } = useContext(DynamicPowerPricesEnabledContext)
  const [selectedDynamicPowerPriceModalSetting, setSelectedDynamicPowerPriceModalSetting] = useState<DynamicPriceType>(DynamicPriceType.Battery)
  const { data, isLoading: isDynamicEmsDataLoading } = useDynamicEmsData(installationId)
  const isDynamicEmsEnabled = dynamicPricesSettings?.isDynamicEmsEnabledForBattery
  const { isMobile: isDownSm } = useApp()
  const { isDynamicEmsV2Accessible } = useIsDynamicEmsAccessible()
  const { getTooltipValues, headerValues } = usePowerPriceChartHeaderValues({ currentPowerPriceValues })

  /* props are stale when the modal is open and values are changed in the input field. so we need local state to update reference lines immediately */
  const [currentBatteryLimit, setCurrentBatteryLimit] = useState(batteryLimit)
  const [currentWallboxLimit, setCurrentWallboxLimit] = useState(wallboxLimit)

  const handleSettingsUpdate = async (newValues: DynamicPricesSettings): Promise<void> => {
    const prevBattery = currentBatteryLimit
    const prevWallbox = currentWallboxLimit
    setCurrentBatteryLimit(Number(newValues.priceLimitBattery ?? batteryLimit))
    setCurrentWallboxLimit(Number(newValues.priceLimitWallbox ?? wallboxLimit))
    try {
      await handleDynamicPricesSettingsUpdate(newValues)
    } catch (e) {
      /* fallback to previous values if update fails */
      setCurrentBatteryLimit(prevBattery)
      setCurrentWallboxLimit(prevWallbox)
      /* throw error so that it is handled by the parent function */
      throw e
    }
  }

  const isBothDynamicPowerPriceEnabled = (isDynamicPowerPriceEnabledForBattery || isDynamicEmsEnabled) && isDynamicPowerPriceEnabledForWallboxes
  const isBatterySelected = selectedDynamicPowerPriceModalSetting === DynamicPriceType.Battery

  const iconColor =
    (isBothDynamicPowerPriceEnabled &&
      selectedDynamicPowerPriceModalSetting === DynamicPriceType.Wallbox &&
      currentPowerPriceValues.price > currentWallboxLimit) ||
    (isBothDynamicPowerPriceEnabled &&
      selectedDynamicPowerPriceModalSetting === DynamicPriceType.Battery &&
      currentPowerPriceValues.price > currentBatteryLimit) ||
    (!isBothDynamicPowerPriceEnabled && isDynamicPowerPriceEnabledForWallboxes && currentPowerPriceValues.price > currentWallboxLimit) ||
    (!isBothDynamicPowerPriceEnabled && isDynamicPowerPriceEnabledForBattery && currentPowerPriceValues.price > currentBatteryLimit)
      ? 'warning'
      : 'primary'

  const isNoDataAvailable = !isPowerPricesLoading && (!powerPrices || powerPrices.length === 0)

  return (
    <ResponsiveModalDrawerContainer modalMaxWidth="md" onClose={onClose}>
      <BannerQueue location={BannerLocations.SettingsOverview} />
      <DialogTitle className={classes.title}>
        <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
          <Typography variant="h2" paddingRight={2}>
            {t('dashboard.liveView.widgets.dynamic-power-price.title')}
          </Typography>
          {!isDynamicEmsEnabled && (
            <DynamicPriceIcon
              className={classes.icon}
              color={iconColor}
              sx={theme => ({ fontSize: 30, '&&': { color: theme.palette[iconColor].main } })}
            />
          )}
        </Box>
        <IconButton sx={{ color: 'grey.grey00' }} onClick={onClose}>
          <CloseIcon className={classes.icon} />
        </IconButton>
      </DialogTitle>
      <Box sx={{ overflow: isDownSm ? 'auto' : '', height: '100%' }}>
        <Box id={POWER_PRICE_CHART_TO_PRINT_ID} height="100%" display="flex" flexDirection="column">
          {!isNoDataAvailable && (
            <PowerPriceChartHeader
              prices={powerPrices || []}
              lastFetchedTimestamp={lastFetchedTimestamp}
              installationId={installationId}
              storageId={storageId}
              isLoading={isPowerPricesLoading}
              {...headerValues}
            />
          )}
          <Box className={classes.body}>
            {isNoDataAvailable ? (
              <Box className={classes.noDataWrapper}>
                <J2Caution />
                <Typography variant="body1" sx={{ width: 'max-content' }}>
                  {t('dashboard.liveView.widgets.dynamic-power-price.no-data-modal-text')}
                </Typography>
              </Box>
            ) : (
              <>
                <Box className={classes.powerPriceChartContainer}>
                  {!isPowerPricesLoading && powerPrices ? (
                    <PowerPriceChart
                      to={toDateForModal.toISOString()}
                      powerPrices={powerPrices}
                      currentPowerPriceValues={currentPowerPriceValues}
                      wallboxLimit={currentWallboxLimit}
                      batteryLimit={currentBatteryLimit}
                      selectedDynamicPowerPriceModalSetting={selectedDynamicPowerPriceModalSetting}
                      getTooltipValues={getTooltipValues}
                      isDynamicEmsEnabled={isDynamicEmsEnabled}
                      dynamicEmsData={data}
                      isDynamicEmsDataLoading={isDynamicEmsDataLoading}
                      isModal
                      minChartHeight="250px"
                      isDynamicPowerPriceEnabledForBattery={isDynamicPowerPriceEnabledForBattery}
                      isDynamicPowerPriceEnabledForWallboxes={isDynamicPowerPriceEnabledForWallboxes}
                    />
                  ) : (
                    <LineChartSkeleton />
                  )}
                </Box>

                {!isDynamicEmsV2Accessible && isDynamicEmsEnabled && isBatterySelected && (
                  <DynamicEmsChartLegend isDynamicEmsDataLoading={isDynamicEmsDataLoading} />
                )}
              </>
            )}
            {isBothDynamicPowerPriceEnabled && (
              <Box className={classes.buttonWrapper}>
                <Box className={classes.buttonGroup}>
                  <TabButtonGroup
                    active={selectedDynamicPowerPriceModalSetting}
                    options={[
                      {
                        id: '1',
                        label: t('common.battery'),
                        onChange: () => setSelectedDynamicPowerPriceModalSetting(DynamicPriceType.Battery),
                      },
                      {
                        id: '0',
                        label: t('common.wallbox'),
                        onChange: () => setSelectedDynamicPowerPriceModalSetting(DynamicPriceType.Wallbox),
                      },
                    ]}
                    width="50%"
                  />
                </Box>
              </Box>
            )}
            <PowerPriceLimitSettingsForm
              selectedDynamicPowerPriceModalSetting={selectedDynamicPowerPriceModalSetting}
              onClose={onClose}
              disabled={!hasInstallationWritingRights}
              dynamicPricesSettings={dynamicPricesSettings}
              handleDynamicPricesSettingsUpdate={handleSettingsUpdate}
              isDynamicEmsEnabled={isDynamicEmsEnabled}
              isDynamicEmsDataLoading={isDynamicEmsDataLoading}
            />
          </Box>
        </Box>
      </Box>
    </ResponsiveModalDrawerContainer>
  )
}

export default DynamicPowerPriceModal
